import React from "react";
import { Paper, Group, Text, Stack, Badge, SimpleGrid } from "@mantine/core";
import MouHoursCard from "./MouHoursCard";

interface Props {
  batchData: any; // Batch data from initialBatchData
}

const CurrentStateFromBatch = ({ batchData }: Props) => {
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Completed: "bg-green-500",
      Hold: "bg-yellow-500",
      "On-going": "bg-[#FC7A69]",
      "Not Started": "bg-gray-400",
      Absconded: "bg-red-500",
      Discarded: "bg-[#221E45]",
    };
    return colors[status] || "bg-gray-400";
  };

  const studentsCount = batchData?.students?.length || 0;
  const instructorsList = batchData?.instructors || batchData?.instructor || [];

  return (
    <Paper
      shadow="md"
      className="p-4 sm:p-6 bg-gradient-to-br from-[#FC7A69]/10 to-[#221E45]/10 border-2 border-[#FC7A69]/30"
      radius="lg"
    >
      {/* HEADER */}
      <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#FC7A69] animate-pulse" />
          <Text className="text-base sm:text-lg font-bold text-[#221E45]">
            Current Batch State
          </Text>
        </div>

        <Badge
          size="md"
          className={`${getStatusColor(batchData?.batchStatus || "Not Started")} text-white text-xs px-3`}
          radius="md"
        >
          {batchData?.batchStatus || "Not Started"}
        </Badge>
      </Group>

      {/* STATS */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
        <Paper
          className="p-3 border border-[#FC7A69]/20 bg-white/50"
          radius="md"
        >
          <Text className="text-xs text-[#221E45]/60 mb-1 font-medium">
            Batch Number
          </Text>
          <Text className="text-sm font-bold text-[#221E45]">
            #{batchData?.batch_no}
          </Text>
        </Paper>

        <Paper
          className="p-3 border border-[#FC7A69]/20 bg-white/50"
          radius="md"
        >
          <Text className="text-xs text-[#221E45]/60 mb-1 font-medium">
            Total Sessions
          </Text>
          <Text className="text-xl font-bold text-[#FC7A69]">
            {batchData?.numSessions || 0}
          </Text>
        </Paper>

        <Paper
          className="p-3 border border-[#FC7A69]/20 bg-white/50"
          radius="md"
        >
          <Text className="text-xs text-[#221E45]/60 mb-1 font-medium">
            Current Trainer
          </Text>
          <Text className="text-sm font-bold text-[#221E45] truncate">
            {batchData?.trainerName || "N/A"}
          </Text>
          <Text className="text-xs text-[#221E45]/50 truncate">
            {batchData?.trainerEmail || ""}
          </Text>
          <Badge size="xs" color="blue" variant="light" mt={4}>
            Active Trainer
          </Badge>
        </Paper>

        <Paper
          className="p-3 border border-[#FC7A69]/20 bg-white/50"
          radius="md"
        >
          <Text className="text-xs text-[#221E45]/60 mb-1 font-medium">
            Batch Details
          </Text>
          <Stack gap={2}>
            <Text className="text-xs">
              <span className="font-bold text-[#FC7A69]">{studentsCount}</span>
              <span className="ml-1 text-[#221E45]/50">Students Enrolled</span>
            </Text>

            <Text className="text-xs text-[#221E45]/60 mb-1">
              Instructors ({instructorsList.length})
            </Text>

            {instructorsList.slice(0, 2).map((ins: any, idx: number) => (
              <Text key={idx} className="text-xs">
                <span className="font-bold text-[#221E45]">{ins.name}</span>
                {/* attendedHours is edited from Journey / Batch Table (manual, logged) */}
                <span className="text-[#FC7A69] ml-1">
                  ({ins.attendedHours || "0"} hrs)
                </span>
              </Text>
            ))}

            {instructorsList.length > 2 && (
              <Text size="xs" c="dimmed">
                +{instructorsList.length - 2} more instructor(s)
              </Text>
            )}
          </Stack>
        </Paper>

        {/* MOU Hours — current trainer */}
        <MouHoursCard batchData={batchData} />
      </SimpleGrid>

      {/* Course Info */}
      {batchData?.courseName && (
        <Paper
          className="p-3 mt-4 border-l-4 border-[#FC7A69] bg-white/70"
          radius="md"
        >
          <Text className="text-xs font-semibold text-[#221E45] mb-1">
            Course
          </Text>
          <Text className="text-xs text-[#221E45]/80">
            {batchData.courseName}
          </Text>
        </Paper>
      )}

      {/* Start Date */}
      {batchData?.startDate && (
        <Paper
          className="p-3 mt-2 border-l-4 border-[#221E45] bg-white/70"
          radius="md"
        >
          <Text className="text-xs font-semibold text-[#221E45] mb-1">
            Batch Start Date
          </Text>
          <Text className="text-xs text-[#221E45]/80">
            {new Date(batchData.startDate).toLocaleDateString("en-US", {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </Text>
        </Paper>
      )}
    </Paper>
  );
};

export default CurrentStateFromBatch;
